import { ComponentType } from 'react'
import { Image03, Images, VideoOn, ClockCheck, Email } from 'blaze-ui/icons/24'
import { Iphone02 } from 'blaze-ui/icons/16'
import { File02 } from 'blaze-ui/icons/20'
import { Cursor04 } from 'blaze-ui/icons/20'
import { ImageAvatar } from 'blaze-ui/icons'
import { DesignChannel, AD_DESIGN_CHANNELS } from 'components/NewFileModal/Types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import Styles from './ContentTypeIcon.module.scss'

const ICON_SIZE = 15

interface IconConfig {
  icon: ComponentType<{ size?: number; color?: string }>
  color: string
}

const CHANNEL_ICON_MAP: Record<string, IconConfig> = {
  [DesignChannel.InstagramStory]: { icon: ClockCheck, color: '#FF37CA' },
  [DesignChannel.FacebookStory]: { icon: ClockCheck, color: '#FF37CA' },
  [DesignChannel.InstagramReel]: { icon: Iphone02, color: '#00AAFF' },
  [DesignChannel.TikTokPost]: { icon: Iphone02, color: '#00AAFF' },
  [DesignChannel.YoutubeShort]: { icon: Iphone02, color: '#00AAFF' },
  [ChannelType.BlogPost]: { icon: File02, color: 'var(--green)' },
  [ChannelType.Newsletter]: { icon: Email, color: '#FFAE00' },
}

const CHANNEL_LABEL_MAP: Record<string, string> = {
  [DesignChannel.InstagramStory]: 'story',
  [DesignChannel.FacebookStory]: 'story',
  [DesignChannel.InstagramReel]: 'short form video',
  [DesignChannel.TikTokPost]: 'short form video',
  [DesignChannel.YoutubeShort]: 'short form video',
  [ChannelType.BlogPost]: 'blog post',
  [ChannelType.Newsletter]: 'email',
}

const CAROUSEL_ICON: IconConfig = { icon: Images, color: 'var(--starter)' }
const AVATAR_VIDEO_ICON: IconConfig = { icon: ImageAvatar, color: '#6A00FF' }
const META_ADS_ICON: IconConfig = { icon: Cursor04, color: '#00A3A0' }
const FEED_VIDEO_ICON: IconConfig = { icon: VideoOn, color: 'var(--upgrade)' }
const STILL_IMAGE_ICON: IconConfig = { icon: Image03, color: '#FD4242' }

interface ContentTypeIconProps {
  channel?: ChannelType | DesignChannel | GenerationType | null
  almanacFile?: AlmanacFileShowItem
}

const isVideoContent = (almanacFile?: AlmanacFileShowItem) =>
  almanacFile?.asset_type === 'Video' ||
  almanacFile?.source_media_info?.mime_type?.startsWith('video') ||
  almanacFile?.content_type === 'video' ||
  !!almanacFile?.duration

export const getContentTypeLabel = (
  channel?: string | null,
  almanacFile?: AlmanacFileShowItem,
  omitFeedPrefix?: boolean,
): string => {
  if (almanacFile?.is_carousel || almanacFile?.content_type === 'carousel') return 'carousel'
  if (almanacFile?.content_type === 'avatar_video') return 'AI avatar video'
  if (channel && CHANNEL_LABEL_MAP[channel]) return CHANNEL_LABEL_MAP[channel]
  if (isVideoContent(almanacFile)) return omitFeedPrefix ? 'video post' : 'feed video post'
  return 'still image'
}

export const getContentTypeIcon = (channel?: string | null, almanacFile?: AlmanacFileShowItem): IconConfig => {
  if (almanacFile?.is_carousel || almanacFile?.content_type === 'carousel') return CAROUSEL_ICON
  if (almanacFile?.content_type === 'avatar_video') return AVATAR_VIDEO_ICON
  if (channel && AD_DESIGN_CHANNELS.includes(channel)) return META_ADS_ICON
  if (channel && CHANNEL_ICON_MAP[channel]) return CHANNEL_ICON_MAP[channel]
  if (isVideoContent(almanacFile)) return FEED_VIDEO_ICON
  return STILL_IMAGE_ICON
}

export const ContentTypeIcon = ({ channel, almanacFile }: ContentTypeIconProps) => {
  const { icon: Icon, color } = getContentTypeIcon(channel as string, almanacFile)

  return (
    <div className={Styles.contentTypeIcon}>
      <Icon size={ICON_SIZE} color={color} />
    </div>
  )
}
